import React from 'react';
import { Check, Mail, AlertCircle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const ConfirmationDialog = ({ isOpen, onClose, type, message }) => {
  const { language } = useLanguage();

  const t = {
    en: {
      success: 'Success!',
      newsletter: 'Newsletter Subscription Confirmed',
      contact: 'Message Sent Successfully',
      newsletterSteps: [
        'Your subscription has been confirmed',
        'You will receive weekly/monthly flight deals',
        'Check your email for confirmation',
        'Watch for our first deal alert soon!'
      ],
      contactSteps: [
        'Your message has been received',
        'We\'ll review your inquiry',
        'Expect a response within 24 hours',
        'Check your email for our reply'
      ],
      close: 'Got it!',
      checkEmail: 'Please check your email inbox'
    },
    ar: {
      success: 'نجح!',
      newsletter: 'تأكيد الاشتراك في النشرة البريدية',
      contact: 'تم إرسال الرسالة بنجاح',
      newsletterSteps: [
        'تم تأكيد اشتراكك',
        'ستتلقى عروض الطيران أسبوعياً/شهرياً',
        'تحقق من بريدك الإلكتروني للتأكيد',
        'انتظر أول تنبيه صفقة قريباً!'
      ],
      contactSteps: [
        'تم استلام رسالتك',
        'سنراجع استفسارك',
        'توقع رداً خلال 24 ساعة',
        'تحقق من بريدك للرد'
      ],
      close: 'حسناً!',
      checkEmail: 'يرجى التحقق من صندوق بريدك'
    }
  };

  const content = t[language];
  const steps = type === 'newsletter' ? content.newsletterSteps : content.contactSteps;
  const title = type === 'newsletter' ? content.newsletter : content.contact;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 animate-fadeIn">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 animate-slideUp" dir={language === 'ar' ? 'rtl' : 'ltr'}>
        {/* Success Icon */}
        <div className="flex justify-center mb-6">
          <div className="bg-green-100 rounded-full p-4">
            <Check className="w-12 h-12 text-green-600" />
          </div>
        </div>

        {/* Title */}
        <h2 className="text-2xl font-bold text-center mb-4 text-gray-800">
          {content.success}
        </h2>
        <h3 className="text-lg font-semibold text-center mb-6 text-gray-700">
          {title}
        </h3>

        {/* Message */}
        {message && (
          <p className="text-gray-600 text-center mb-6">
            {message}
          </p>
        )}

        {/* Steps */}
        <div className="space-y-3 mb-6">
          {steps.map((step, index) => (
            <div key={index} className="flex items-start gap-3">
              <div className="bg-orange-100 rounded-full p-1 flex-shrink-0 mt-1">
                <Check className="w-4 h-4 text-orange-600" />
              </div>
              <span className="text-gray-700">{step}</span>
            </div>
          ))}
        </div>

        {/* Email Reminder */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-2 mb-2">
            <Mail className="w-5 h-5 text-blue-600" />
            <span className="font-semibold text-blue-800">{content.checkEmail}</span>
          </div>
          <p className="text-sm text-blue-700">
            {language === 'en' 
              ? 'A confirmation email has been sent to your inbox.'
              : 'تم إرسال بريد تأكيد إلى صندوقك.'}
          </p>
        </div>

        {/* Close Button */}
        <button
          onClick={onClose}
          className="w-full bg-gradient-to-r from-orange-500 via-pink-500 to-purple-600 text-white font-bold py-3 px-6 rounded-lg hover:shadow-xl transition-all duration-300"
        >
          {content.close}
        </button>
      </div>
    </div>
  );
};

export default ConfirmationDialog;